"use client"

import { useCallback, useEffect, useMemo, useState } from "react"
import { useRouter } from "next/navigation"
import { AnimatePresence, motion } from "framer-motion"
import { CalendarDays, Megaphone, UtensilsCrossed, Users, X } from "lucide-react"
import { useApp } from "@/lib/context"
import { capaDoBufete, pratosDoBufete } from "@/lib/api"
import { useModalA11y } from "@/lib/use-modal-a11y"
import { formatPrice } from "@/lib/utils"

const DISMISS_KEY = "buffete_intro_dismissed"

function scrollToId(id: string) {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" })
}

/**
 * Bufete em destaque â€” aparece em TODOS os temas.
 * - Convite em ecrÃ£ inteiro logo ao iniciar (dispensÃ¡vel)
 * - Banner persistente com "Comprar Já!" e "Saber mais..."
 */
export default function BuffetAd() {
  const router = useRouter()
  const { buffete, addBuffeteToDraft, showNotification } = useApp()
  const [showIntro, setShowIntro] = useState(false)
  const [showDetails, setShowDetails] = useState(false)
  const [imageReady, setImageReady] = useState(false)

  const closeIntro = useCallback(() => {
    sessionStorage.setItem(DISMISS_KEY, "1")
    setShowIntro(false)
  }, [])
  const closeDetails = useCallback(() => setShowDetails(false), [])

  const introRef = useModalA11y<HTMLDivElement>(showIntro, closeIntro)
  const detailsRef = useModalA11y<HTMLDivElement>(showDetails, closeDetails)

  useEffect(() => {
    if (!buffete) return
    if (sessionStorage.getItem(DISMISS_KEY)) return
    setShowIntro(true)
  }, [buffete])

  const precoMin = useMemo(
    () => buffete?.preco ?? null,
    [buffete],
  )

  if (!buffete) return null

  // Capa: imagem prÃ³pria do bufete ou a foto do primeiro prato incluÃ­do
  const cover = capaDoBufete(buffete)
  const pratosIncluidos = pratosDoBufete(buffete)

  /** Texto do perÃ­odo de validade, quando o bufete Ã© temporÃ¡rio.
   * Aceita "YYYY-MM-DD" ou ISO completo (o backend devolve Date). */
  const periodoValidade = (() => {
    if (!buffete.dataInicio && !buffete.dataFim) return null
    const fmt = (iso?: string | null): string | null => {
      if (!iso) return null
      const d = new Date(iso.length === 10 ? `${iso}T12:00:00` : iso)
      if (Number.isNaN(d.getTime())) return null
      return d.toLocaleDateString("pt-BR", { day: "numeric", month: "short" })
    }
    const inicio = fmt(buffete.dataInicio)
    const fim = fmt(buffete.dataFim)
    if (inicio && fim) return `De ${inicio} a ${fim}`
    if (inicio) return `A partir de até ${inicio}`
    if (fim) return ` até ${fim}`
    return null
  })()

  const comprar = (e?: React.MouseEvent) => {
    e?.stopPropagation()
    if (!buffete) {
      // Sem bufete carregado nÃ£o hÃ¡ nada para pedir â€” nÃ£o deixar o utilizador
      // cair na pÃ¡gina de pedido vazia sem explicaÃ§Ã£o
      showNotification("Bufete indisponÃ­vel neste momento.")
      return
    }
    addBuffeteToDraft()
    sessionStorage.setItem(DISMISS_KEY, "1")
    setShowIntro(false)
    setShowDetails(false)
    // O pedido acontece na pÃ¡gina completa
    router.push("/pedido")
  }

  return (
    <>
      {/* ===== Convite inicial em ecrÃ£ inteiro ===== */}
      <AnimatePresence>
        {showIntro && (
          <motion.div
            ref={introRef}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-[70] bg-black/85 backdrop-blur-md"
            role="dialog"
            aria-modal="true"
            aria-label={buffete.nome}
          >
            {cover && (
              <motion.img
                src={cover}
                alt={buffete.nome}
                initial={{ scale: 1.08, opacity: 0 }}
                animate={imageReady ? { scale: 1, opacity: 0.8 } : {}}
                transition={{ duration: 0.9, ease: "easeOut" }}
                onLoad={() => setImageReady(true)}
                onError={(e) => {
                  e.currentTarget.style.display = "none"
                  setImageReady(true)
                }}
                className="absolute inset-0 h-full w-full object-cover"
              />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/25" />

            <button
              onClick={closeIntro}
              aria-label="Fechar anúncio do bufete"
              className="absolute right-4 top-4 z-10 rounded-full bg-white/10 p-2 text-white/80 backdrop-blur transition-colors hover:bg-white/20 hover:text-white"
            >
              <X className="size-5" />
            </button>

            <div className="absolute inset-x-0 bottom-0 px-5 pb-10 safe-bottom sm:px-8">
              <motion.div
                initial={{ opacity: 0, y: 28 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.25, duration: 0.6 }}
                className="mx-auto max-w-xl"
              >
                {/* Selo do bufete */}
                <div
                  className="liquid-sheen mb-4 inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.22em] text-white shadow-lg"
                  style={{ background: "linear-gradient(120deg, hsl(24 94% 50%), hsl(16 92% 50%))" }}
                >
                  <Megaphone className="size-3.5" aria-hidden="true" />
                  Bufete
                </div>

                <h2 className="font-serif text-4xl font-light leading-tight text-white drop-shadow-[0_2px_16px_rgba(0,0,0,0.6)] sm:text-5xl">
                  {buffete.nome}
                </h2>

                {buffete.descricao && (
                  <p className="mt-2 line-clamp-3 max-w-md text-sm font-light leading-relaxed text-white/75">
                    {buffete.descricao}
                  </p>
                )}

                <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-white/90">
                  <span className="flex items-center gap-1.5">
                    <UtensilsCrossed className="size-4 text-primary" aria-hidden="true" />
                    {buffete.quantidadePratos} pratos incluidos
                  </span>
                  {precoMin != null && (
                    <span className="font-serif text-lg">{formatPrice(precoMin)}</span>
                  )}
                  {periodoValidade && (
                    <span className="flex items-center gap-1.5 rounded-full bg-white/10 px-2.5 py-1 text-xs backdrop-blur">
                      <CalendarDays className="size-3.5" aria-hidden="true" />
                      {periodoValidade}
                    </span>
                  )}
                </div>

                <div className="mt-6 flex flex-wrap items-center gap-3">
                  <button
                    onClick={(e) => comprar(e)}
                    className="rounded-full px-7 py-3 text-sm font-semibold text-white shadow-lg shadow-black/40 transition-transform hover:scale-[1.03] active:scale-95 liquid-sheen"
                    style={{ background: "linear-gradient(120deg, hsl(24 94% 50%), hsl(16 92% 50%))" }}
                  >
                    Comprar Já!
                  </button>
                  <button
                    onClick={() => {
                      sessionStorage.setItem(DISMISS_KEY, "1")
                      setShowIntro(false)
                      setShowDetails(true)
                    }}
                    className="glass rounded-full px-6 py-3 text-sm font-light tracking-wide text-white transition-colors hover:bg-white/15"
                  >
                    Saber mais...
                  </button>
                </div>

                <button
                  onClick={closeIntro}
                  className="mt-4 block text-xs font-light tracking-wide text-white/50 underline-offset-4 transition-colors hover:text-white/80"
                >
                  Explorar mais...
                </button>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ===== Banner persistente ===== */}
      <section id="sec-bufete" className="scroll-mt-16 px-4 pt-6 sm:px-6">
        <div
          className="liquid-sheen relative mx-auto max-w-4xl overflow-hidden rounded-2xl text-white shadow-lg"
          style={{ background: "linear-gradient(120deg, hsl(24 94% 46%), hsl(16 92% 48%))" }}
        >
          {cover && (
            <img
              src={cover}
              alt=""
              aria-hidden="true"
              className="absolute inset-0 h-full w-full object-cover opacity-25 mix-blend-overlay"
              onError={(e) => {
                e.currentTarget.style.display = "none"
              }}
            />
          )}

          <div className="relative flex flex-wrap items-center gap-4 p-5 sm:p-6">
            <span className="flex size-11 shrink-0 items-center justify-center rounded-full bg-white/15 backdrop-blur">
              <Megaphone className="size-5" aria-hidden="true" />
            </span>

            <div className="min-w-0 flex-1">
              <p className="text-[0.65rem] font-semibold uppercase tracking-[0.24em] text-white/80">Bufete</p>
              <h3 className="truncate font-serif text-xl font-light sm:text-2xl">{buffete.nome}</h3>
              <p className="mt-0.5 flex items-center gap-3 text-xs text-white/85">
                <span className="flex items-center gap-1">
                  <Users className="size-3.5" aria-hidden="true" />
                  {buffete.quantidadePratos} pratos
                </span>
                <span className="font-serif text-base">{formatPrice(buffete.preco)}</span>
              </p>
            </div>

            <div className="flex w-full shrink-0 gap-2 sm:w-auto">
              <button
                onClick={(e) => comprar(e)}
                className="flex-1 rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-primary shadow transition-transform hover:scale-[1.02] active:scale-95 sm:flex-none"
              >
                Comprar Já!
              </button>
              <button
                onClick={() => setShowDetails(true)}
                className="flex-1 rounded-full border border-white/50 bg-white/10 px-5 py-2.5 text-sm font-light text-white backdrop-blur transition-colors hover:bg-white/20 sm:flex-none"
              >
                Saber mais...
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ===== Modal "Saber mais" ===== */}
      <AnimatePresence>
        {showDetails && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] flex items-end justify-center bg-black/70 backdrop-blur-sm sm:items-center"
            onClick={closeDetails}
          >
            <motion.div
              ref={detailsRef}
              role="dialog"
              aria-modal="true"
              aria-label={buffete.nome}
              initial={{ y: 60, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 60, opacity: 0 }}
              transition={{ type: "spring", damping: 26, stiffness: 300 }}
              className="max-h-[92vh] w-full overflow-y-auto rounded-t-3xl bg-card text-card-foreground shadow-2xl sm:max-w-lg sm:rounded-3xl"
              onClick={(e) => e.stopPropagation()}
            >
              {cover ? (
                <div className="relative h-44 overflow-hidden sm:h-52">
                  <img src={cover} alt={buffete.nome} className="h-full w-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <span
                    className="absolute left-4 top-4 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[0.65rem] font-semibold uppercase tracking-[0.2em] text-white"
                    style={{ background: "linear-gradient(120deg, hsl(24 94% 50%), hsl(16 92% 50%))" }}
                  >
                    <Megaphone className="size-3" aria-hidden="true" /> Bufete
                  </span>
                </div>
              ) : null}

              <div className="p-5 sm:p-6">
                <div className="flex items-start justify-between gap-3">
                  <h3 className="font-serif text-2xl font-light">{buffete.nome}</h3>
                  <button
                    onClick={closeDetails}
                    className="rounded-full bg-muted p-2 text-muted-foreground transition-colors hover:bg-muted/70"
                    aria-label="Fechar"
                  >
                    <X className="size-4" />
                  </button>
                </div>

                {buffete.descricao && (
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{buffete.descricao}</p>
                )}

                <div className="mt-4 grid grid-cols-2 gap-3">
                  <div className="rounded-xl bg-secondary p-3 text-center">
                    <UtensilsCrossed className="mx-auto mb-1 size-4 text-primary" aria-hidden="true" />
                    <p className="font-serif text-xl">{buffete.quantidadePratos}</p>
                    <p className="text-xs text-muted-foreground">pratos inclui­dos</p>
                  </div>
                  <div className="rounded-xl bg-secondary p-3 text-center">
                    <p className="mt-1 font-serif text-xl text-primary">{formatPrice(buffete.preco)}</p>
                    <p className="text-xs text-muted-foreground">Preço Unico</p>
                  </div>
                </div>

                {/* PerÃ­odo de validade do bufete temporÃ¡rio */}
                {periodoValidade && (
                  <p className="mt-3 flex items-center justify-center gap-1.5 rounded-full bg-primary/10 px-3 py-1.5 text-xs font-medium text-primary">
                    <CalendarDays className="size-3.5" aria-hidden="true" />
                    {periodoValidade}
                  </p>
                )}

                {/* O que estÃ¡ incluÃ­do â€” pratos com a quantidade de cada um */}
                {pratosIncluidos.length > 0 ? (
                  <div className="mt-5">
                    <h4 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                      <UtensilsCrossed className="size-3.5" aria-hidden="true" /> O que está incluido
                    </h4>
                    <ul className="mt-3 space-y-1.5">
                      {pratosIncluidos.map((p, i) => (
                        <li
                          key={`${p.nome}-${i}`}
                          className="flex items-center gap-3 rounded-xl border border-border/70 px-2.5 py-2"
                        >
                          {p.urls[0] && !p.urls[0].includes("placeholder") ? (
                            <img
                              src={p.urls[0]}
                              alt=""
                              loading="lazy"
                              className="size-10 shrink-0 rounded-lg object-cover"
                            />
                          ) : (
                            <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-secondary text-muted-foreground/60">
                              <UtensilsCrossed className="size-4" aria-hidden="true" />
                            </span>
                          )}
                          <span className="min-w-0 flex-1 truncate text-sm">{p.nome}</span>
                          <span
                            className="shrink-0 rounded-full px-2 py-0.5 text-[0.65rem] font-bold text-white"
                            style={{ background: "linear-gradient(120deg, hsl(24 94% 50%), hsl(16 92% 50%))" }}
                            title={`Quantidade disponÃ­vel neste bufete`}
                          >
                            {p.quantidade}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <p className="mt-4 rounded-xl border border-dashed border-border p-3 text-center text-xs text-muted-foreground">
                    Bufete com {buffete.quantidadePratos} pratos preparados na hora â€” pergunta ao
                    nosso staff o menu do dia.
                  </p>
                )}

                <button
                  onClick={() => {
                    comprar()
                    setShowDetails(false)
                  }}
                  className="mt-5 w-full rounded-full py-3.5 text-sm font-semibold text-white shadow-lg transition-transform hover:scale-[1.01] active:scale-[0.99]"
                  style={{ background: "linear-gradient(120deg, hsl(24 94% 50%), hsl(16 92% 50%))" }}
                >
                  Comprar Já!  {formatPrice(buffete.preco)}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}
